import { grpc } from '@improbable-eng/grpc-web';

// ==============================|| PROMPTS — WAIT FOR THE PAGE ||============================== //
// A prompt opens once the page has loaded: no server call in flight for a short quiet window.

// The usual "network idle" window: long enough for a page's next call to start after one ends.
export const PAGE_QUIET_MS = 500;
// A page still loading after this long is as ready as it will get, so the prompt stops waiting.
export const SETTLE_MAX_MS = 10_000;

let callsInFlight = 0;
const listeners = new Set<() => void>();
const changeInFlight = (by: number) => {
  callsInFlight += by;
  listeners.forEach((listener) => listener());
};

/** Wraps a transport so each call it carries is counted from start to end (or cancel). */
export const countingTransport =
  (base: grpc.TransportFactory): grpc.TransportFactory =>
  (options) => {
    // A stream stays open, so counting it would keep every page loading.
    if (options.methodDefinition.responseStream) return base(options);
    let counted = false;
    const finish = () => {
      if (!counted) return;
      counted = false;
      changeInFlight(-1);
    };
    const transport = base({
      ...options,
      onEnd: (err) => {
        finish();
        options.onEnd(err);
      }
    });
    return {
      start: (metadata) => {
        counted = true;
        changeInFlight(1);
        transport.start(metadata);
      },
      sendMessage: (bytes) => transport.sendMessage(bytes),
      finishSend: () => transport.finishSend(),
      cancel: () => {
        finish();
        transport.cancel();
      }
    };
  };

/** Counts every server call the app makes; run once at start-up, before the first call. */
export const trackServerCalls = (): void =>
  // The library's own default transport, counted.
  grpc.setDefaultTransport(countingTransport(grpc.CrossBrowserHttpTransport({ withCredentials: false })));

/** Calls onSettled once the page has loaded, or after SETTLE_MAX_MS at most; returns a cancel. */
export function whenPageSettled(onSettled: () => void): () => void {
  let quiet: number | undefined;
  const restart = () => {
    window.clearTimeout(quiet);
    quiet = callsInFlight > 0 ? undefined : window.setTimeout(settle, PAGE_QUIET_MS);
  };
  const ceiling = window.setTimeout(settle, SETTLE_MAX_MS);
  const cancel = () => {
    window.clearTimeout(quiet);
    window.clearTimeout(ceiling);
    listeners.delete(restart);
  };
  function settle() {
    cancel();
    onSettled();
  }
  listeners.add(restart);
  restart();
  return cancel;
}
